// Swell's own (first-party) integrations.
//
// `id` is the key a service is stored under in settings at
// `integrations.services.<id>`, and it is also the value an app puts in its
// swell.json `integrations` array to declare that it iterates to the same
// third-party service. That shared vocabulary is what makes conflict detection
// possible, so this list is plain data with no React in it: the client bundle
// and the server both read it, the same way `shared/app-versions` is shared.
//
// The React-only half of each integration (`renderConnected`) lives in
// utils/integration.js, which merges it back onto these entries.
const SWELL_INTEGRATIONS = [
  {
    id: 'avatax',
    name: 'Avalara AvaTax',
    description:
      'Simplify taxes with real-time tax calculations and compliance',
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Synchronize customer emails with your Mailchimp account',
  },
  {
    id: 'taxjar',
    name: 'TaxJar',
    description:
      'Simplify taxes with real-time tax calculations and compliance',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Send store events to your Slack workspace',
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Automate workflows in your store with Zapier',
    externallink:
      'https://zapier.com/developer/public-invite/137804/0ef5156359449b2b1ff8485ffd79bbea/',
  },
  {
    id: 'segment',
    name: 'Segment',
    description: 'Send store events to Segment automatically',
  },
  {
    id: 'omnisend',
    name: 'Omnisend',
    description: 'Synchronize order and customer data with Omnisend',
    sync: true,
  },
  {
    id: 'klaviyo',
    name: 'Klaviyo',
    description: 'Synchronize order and customer data with Klaviyo',
  },
  {
    id: 'algolia',
    name: 'Algolia',
    sync: true,
    description:
      'Improve search functionalities by indexing product data with Algolia',
  },
  // {
  //   id: 'quickbooks',
  //   name: 'QuickBooks',
  //   comingsoon: true,
  //   description: 'Synchronize payments and customer details with QuickBooks',
  // },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    description: 'Optimize email notification delivery with SendGrid',
  },
  {
    id: 'gladly',
    name: 'Gladly',
    description: 'Synchronize customers data with Gladly',
    externallink: 'https://www.swell.is/help/integrations/gladly',
  },
  {
    id: 'contentful',
    name: 'Contentful',
    description: 'Connect store data with Contentful for integrated content',
    externallink: 'https://www.contentful.com/marketplace/app/swell/',
  },
  {
    id: 'shipstation',
    name: 'ShipStation',
    sync: true,
    description: 'Manage and ship your orders with ShipStation',
  },
  {
    id: 'yotpo',
    name: 'Yotpo',
    sync: true,
    description: `Grow your brand by linking your data with Yotpo's marketing tools`,
  },
  {
    id: 'smartystreets',
    name: 'Smarty',
    description: 'US address autocomplete for checkout and customer forms',
  },
];

/**
 * The Swell integration a swell.json `integrations` value names, if any. A
 * value that matches nothing here is still meaningful — apps may name services
 * Swell has no integration for, and those still conflict with each other.
 * @param {string} id
 * @returns {object|undefined}
 */
function getSwellIntegration(id) {
  return SWELL_INTEGRATIONS.find((integration) => integration.id === id);
}

/**
 * @param {string} id
 * @returns {string|undefined}
 */
function swellIntegrationName(id) {
  return getSwellIntegration(id)?.name;
}

/**
 * Whether a service is switched on for the store. Two of them keep the
 * credential that makes them usable outside `activated`, so the flag alone
 * does not mean the integration is live.
 * @param {string} id
 * @param {object} [service] the `integrations.services.<id>` settings object
 * @returns {boolean}
 */
function integrationActivated(id, service = {}) {
  switch (id) {
    case 'mailchimp':
      return Boolean(service.activated && service.access_token);
    case 'slack':
      return Boolean(service.activated && service.installation);
    default:
      return Boolean(service.activated);
  }
}

/**
 * Whether a service is actually running. `activated` means it is connected and
 * configured; the merchant can still leave it switched off, and one that is
 * off processes nothing. Both have to hold for it to contend for a service.
 * @param {string} id
 * @param {object} [service] the `integrations.services.<id>` settings object
 * @returns {boolean}
 */
function integrationLive(id, service = {}) {
  return integrationActivated(id, service) && Boolean(service.enabled);
}

module.exports = {
  SWELL_INTEGRATIONS,
  getSwellIntegration,
  swellIntegrationName,
  integrationActivated,
  integrationLive,
};
